import { useState } from 'react';
import {
  Card, CardContent, Stack, Typography, Table, TableHead, TableRow, TableCell, TableBody, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar, LinearProgress, Divider, Link, TextField, Box,
} from '@mui/material';
import { CheckCircle, Inventory2 } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { dealerOrdersApi, type DealerOrder, type OrderDispatch } from '../../api/dealerOrders.api';
import { FileUploadField } from '../../components/FileUploadField';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';
import { DealerSummaryCard } from '../../components/DealerSummaryCard';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;

/**
 * FULFILMENT → Delivery. Dispatched orders are in-transit. Open one, mark each
 * consignment Delivered and upload the POD (proof of delivery).
 */
export function DeliveryPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [openId, setOpenId] = useState<number | null>(null);
  const [deliverTarget, setDeliverTarget] = useState<OrderDispatch | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [form, setForm] = useState({ receivedBy: '', podFileUrl: '', remarks: '' });

  const list = useQuery({ queryKey: ['delivery', 'in-transit'], queryFn: () => dealerOrdersApi.list({ status: 'dispatched', limit: 100 }) });
  const detail = useQuery({ queryKey: ['delivery', 'order', openId], queryFn: () => dealerOrdersApi.get(openId!), enabled: openId != null });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['delivery'] }); };

  const deliverMut = useMutation({
    mutationFn: () => dealerOrdersApi.deliver(openId!, deliverTarget!.id, { receivedBy: form.receivedBy, podFileUrl: form.podFileUrl || undefined, remarks: form.remarks || undefined }),
    onSuccess: () => { setDeliverTarget(null); setForm({ receivedBy: '', podFileUrl: '', remarks: '' }); setError(null); setToast('Marked delivered'); invalidate(); qc.invalidateQueries({ queryKey: ['delivery', 'order', openId] }); },
    onError: (e) => setError(extractApiError(e)),
  });

  return (
    <>
      <PageHeader title="Delivery" subtitle="Dispatched (in-transit) orders. Mark delivered and upload the POD." />

      <Card variant="outlined"><CardContent>
        <Table size="small">
          <TableHead><TableRow><TableCell>Order</TableCell><TableCell>Order Date</TableCell><TableCell align="right">Grand Total</TableCell><TableCell>Status</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {(list.data?.data ?? []).map((o: DealerOrder) => (
              <TableRow key={o.id} hover>
                <TableCell>{o.orderCode}</TableCell>
                <TableCell>{o.orderDate ?? '—'}</TableCell>
                <TableCell align="right">{inr(o.grandTotal)}</TableCell>
                <TableCell><Chip size="small" color="warning" label={o.status} /></TableCell>
                <TableCell align="right"><Button size="small" startIcon={<Inventory2 />} onClick={() => setOpenId(o.id)}>View / Deliver</Button></TableCell>
              </TableRow>
            ))}
            {(list.data?.data.length ?? 0) === 0 && <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary" py={2} textAlign="center">No in-transit orders.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      {/* Order detail + consignments */}
      <Dialog open={openId != null} onClose={() => setOpenId(null)} fullWidth maxWidth="md">
        <DialogTitle>{detail.data?.orderCode ?? 'Order'} — Consignments</DialogTitle>
        <DialogContent>
          {detail.isLoading && <LinearProgress />}
          {detail.data && <Box sx={{ mb: 1.5 }}><DealerSummaryCard dealerId={detail.data.dealerId} dense /></Box>}
          {detail.data && (
            <Table size="small">
              <TableHead><TableRow><TableCell>Dispatch</TableCell><TableCell>Date</TableCell><TableCell>Transporter</TableCell><TableCell>Docket</TableCell><TableCell>Status</TableCell><TableCell>POD</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {(detail.data.dispatches ?? []).map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>{d.dispatchCode}</TableCell>
                    <TableCell>{d.dispatchDate ?? '—'}</TableCell>
                    <TableCell>{d.transporter ?? '—'}</TableCell>
                    <TableCell>{d.docketNo ?? '—'}</TableCell>
                    <TableCell><Chip size="small" color={d.status === 'delivered' ? 'success' : 'warning'} label={d.status} /></TableCell>
                    <TableCell>{d.podFileUrl ? <Link href={d.podFileUrl} target="_blank" rel="noopener">POD</Link> : '—'}</TableCell>
                    <TableCell align="right">{hasPermission('dealer_dispatch.deliver') && d.status === 'dispatched' && <Button size="small" startIcon={<CheckCircle />} onClick={() => { setDeliverTarget(d); setError(null); }}>Deliver</Button>}</TableCell>
                  </TableRow>
                ))}
                {(detail.data.dispatches?.length ?? 0) === 0 && <TableRow><TableCell colSpan={7}><Typography variant="caption" color="text.secondary">No consignments.</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setOpenId(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Mark delivered */}
      <Dialog open={deliverTarget != null} onClose={() => setDeliverTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Mark Delivered — {deliverTarget?.dispatchCode}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <Stack spacing={1.5} mt={1}>
            <TextField label="Received By" required value={form.receivedBy} onChange={(e) => setForm((f) => ({ ...f, receivedBy: e.target.value }))} fullWidth />
            <FileUploadField label="Proof of Delivery" value={form.podFileUrl || null} onChange={(url) => setForm((f) => ({ ...f, podFileUrl: url ?? '' }))} hint="Upload the proof-of-delivery document/photo" />
            <Divider />
            <TextField label="Delivery Remarks" value={form.remarks} onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))} fullWidth multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeliverTarget(null)}>Cancel</Button>
          <Button variant="contained" startIcon={<CheckCircle />} disabled={!form.receivedBy || deliverMut.isPending} onClick={() => deliverMut.mutate()}>Mark Delivered</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
